import { Field } from "@/components/site/TourInquiryForm";
import { Switch } from "@/components/ui/switch";

export const OTHER = "__other";

const LocationField = ({ label, id, value, other, details, locations, onChange, errors }) => (
  <div className="space-y-3">
    <Field label={label} id={id} error={errors[id]}>
      <select id={id} className="field-input" value={value} onChange={(e) => onChange(id, e.target.value)} data-testid={`booking-${id}-select`}>
        <option value="">Select a location</option>
        {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        <option value={OTHER}>Other location (request a quote)</option>
      </select>
    </Field>
    {value === OTHER && (
      <Field label="Enter location" id={`${id}_other`} error={errors[`${id}_other`]}>
        <input id={`${id}_other`} className="field-input" placeholder="Village, beach, restaurant…" value={other} onChange={(e) => onChange(`${id}_other`, e.target.value)} data-testid={`booking-${id}-other-input`} />
      </Field>
    )}
    <input className="field-input" placeholder="Hotel name or address (optional)" value={details} onChange={(e) => onChange(`${id}_details`, e.target.value)} data-testid={`booking-${id}-details-input`} aria-label={`${label} details`} />
  </div>
);

const ToggleRow = ({ label, hint, checked, onChange, testId, children }) => (
  <div className="card-soft border border-slate-200 p-5">
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="font-medium text-ink">{label}</p>
        <p className="text-sm text-slate-500">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} className="data-[state=checked]:bg-gold" />
    </div>
    {checked && children && <div className="mt-4">{children}</div>}
  </div>
);

const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

export const TripStep = ({ f, set, errors, locations }) => {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="grid gap-6" data-testid="booking-step-trip">
      <div className="grid gap-6 md:grid-cols-2">
        <LocationField label="Pickup location" id="pickup" value={f.pickup} other={f.pickup_other} details={f.pickup_details} locations={locations} onChange={set} errors={errors} />
        <LocationField label="Drop-off location" id="dropoff" value={f.dropoff} other={f.dropoff_other} details={f.dropoff_details} locations={locations} onChange={set} errors={errors} />
      </div>
      <div className="grid gap-6 sm:grid-cols-3">
        <Field label="Date" id="date" error={errors.date}>
          <input id="date" type="date" min={today} className="field-input" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="booking-date-input" />
        </Field>
        <Field label="Time" id="time" error={errors.time}>
          <input id="time" type="time" className="field-input" value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="booking-time-input" />
        </Field>
        <Field label="Flight / ferry no." id="flight_number">
          <input id="flight_number" className="field-input uppercase" placeholder="e.g. BA2524" value={f.flight_number} onChange={(e) => set("flight_number", e.target.value)} data-testid="booking-flight-input" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <Field label="Passengers" id="passengers">
          <select id="passengers" className="field-input" value={f.passengers} onChange={(e) => set("passengers", Number(e.target.value))} data-testid="booking-passengers-select">
            {range(1, 12).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="Suitcases" id="luggage">
          <select id="luggage" className="field-input" value={f.luggage} onChange={(e) => set("luggage", Number(e.target.value))} data-testid="booking-luggage-select">
            {range(0, 12).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
      </div>
      <ToggleRow label="Child seats" hint="Free of charge" checked={f.child_seats} onChange={(v) => set("child_seats", v)} testId="booking-child-seats-switch">
        <select className="field-input" value={f.child_seat_count} onChange={(e) => set("child_seat_count", Number(e.target.value))} data-testid="booking-child-seat-count-select" aria-label="Number of child seats">
          {range(1, 4).map((n) => <option key={n} value={n}>{n} seat{n > 1 ? "s" : ""}</option>)}
        </select>
      </ToggleRow>
      <ToggleRow label="Meet & greet with name sign" hint="We wait at arrivals with your name" checked={f.meet_greet} onChange={(v) => set("meet_greet", v)} testId="booking-meet-greet-switch">
        <input className="field-input" placeholder="Name on the sign (optional)" value={f.name_sign} onChange={(e) => set("name_sign", e.target.value)} data-testid="booking-name-sign-input" aria-label="Name on the sign" />
      </ToggleRow>
    </div>
  );
};

export const CODES = ["+30", "+44", "+353", "+49", "+33", "+39", "+34", "+31", "+32", "+41", "+43", "+45", "+46", "+47", "+358", "+48", "+420", "+36", "+40", "+359", "+972", "+90", "+1", "+61", "+7"];

export const ContactStep = ({ f, set, errors }) => (
  <div className="grid gap-6" data-testid="booking-step-contact">
    <Field label="Full name" id="name" error={errors.name}>
      <input id="name" autoComplete="name" className="field-input" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name-input" />
    </Field>
    <Field label="Email" id="email" error={errors.email}>
      <input id="email" type="email" autoComplete="email" className="field-input" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email-input" />
    </Field>
    <Field label="Phone (with country code)" id="phone" error={errors.phone}>
      <div className="flex gap-2">
        <select className="field-input !w-28 shrink-0" value={f.phone_code} onChange={(e) => set("phone_code", e.target.value)} data-testid="booking-phone-code-select" aria-label="Country code">
          {CODES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input id="phone" type="tel" autoComplete="tel-national" className="field-input" placeholder="Mobile number" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone-input" />
      </div>
    </Field>
    <Field label="Notes for your driver (optional)" id="notes">
      <textarea id="notes" rows={3} className="field-input" placeholder="Extra luggage, a stop on the way, special requests…" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes-input" />
    </Field>
  </div>
);
